import { useState, useCallback } from 'react';

import { noticeError } from '@ratehub/base-ui';

// Used for measuring container size - Re-renders the parent component when the
// ref element changes dimensions. Uses a callback ref to also allow
// re-rendering the parent component when the ref changes.
//
// IMPORTANT: Similar to the react-sizeme package, the returned rect object will
// be null on the first render. Subsequent renders should contain proper
// width/height values though. Please consider how this impacts your component
// regarding server-side renders and SEO.
function useResizeObserver() {
    const [ rect, setRect ] = useState(null);

    const setRef = useCallback(node => {
        if (node === undefined) {
            noticeError(new Error('[useResizeObserver] callback ref was not assigned to an element'));
            return;
        }

        const resizeObserver = new ResizeObserver(entries => {
            if (entries.length > 1) {
                noticeError(new Error('[useResizeObserver] found more than one entry in observer callback.'));
            }

            // 1. ASSUMPTION: entries only has one element in it (the
            // container). Please don't expect this to observe multiple
            // elements at once.
            //
            // 2. We're using borderBoxSize here so the returned
            // width/height will include the container's padding and
            // border (but not margin)
            //
            // 3. Only check the first member of borderBoxSize for now
            // (not supporting fragmented elements like multi-col elements)
            setRect({
                width: entries[0]?.borderBoxSize[0]?.inlineSize,
                height: entries[0]?.borderBoxSize[0]?.blockSize,
            });
        });

        // useCallback passes null when the ref element has been unmounted or
        // the ref has been unassigned.
        if (node !== null) {
            // actually start observing (resizeObserver is created but not observing yet).
            resizeObserver.observe(node);
        } else {
            // clean up the previously created observer.
            resizeObserver?.disconnect();
        }
    }, []);

    return [ rect, setRef ];
}

export default useResizeObserver;
